import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CartItem, Order, OrderStatus } from "@/types/inde_menu";
import type { OrderState } from "@/types/indeMenuStates";
import { generateUniqueId } from "@/helpers/generateUniqueId";
import { validateOrderStatusTransition } from "@/types/status";

// Extend OrderState interface to include subscription methods
interface EnhancedOrderState extends OrderState {
    // State properties
    reviews: Array<{ id: string, userId: string, comment: string, createdAt: string }>;
    // Subscription methods for real-time updates
    subscribeToOrders: (callback: (order: Order) => void) => () => void;
    unsubscribeFromOrders: (callback: (order: Order) => void) => void;
    // Additional real-time methods
    subscribeToOrderUpdates: (orderId: string, callback: (order: Order) => void) => () => void;
    unsubscribeFromOrderUpdates: (orderId: string, callback: (order: Order) => void) => void;
}

let socket: any = null;
let orderSubscribers: Array<(order: Order) => void> = [];
const orderSpecificSubscribers: Record<string, Array<(order: Order) => void>> = {};

const initializeSocket = (): any => {
    console.log('Real-time socket handled by client SocketService');
    return null;
};

const simulateApiDelay = (min = 300, max = 800) =>
    new Promise((resolve) => setTimeout(resolve, Math.random() * (max - min) + min));

// --- ORDER STORE ---
export const useOrderStore = create<EnhancedOrderState>()(
    persist(
        (set, get) => {
            // Initialize socket and subscriptions on first run
            if (typeof window !== "undefined" && !(window as any).__orders_socket_initialized) {
                initializeSocket();
                (window as any).__orders_socket_initialized = true;
            }

            return {
                // =========================
                // STATE
                // =========================
                orders: [],
                currentOrder: null,
                isLoading: false,
                reviews: [],
                error: null,
                filter: "all",
                sort: "newest",
                searchQuery: "",
                dateRange: { start: null, end: null },
                pagination: { page: 1, limit: 10, total: 0 },
                currentUserId: undefined,

                setCurrentUserId: (userId: string | undefined) => set({ currentUserId: userId }),

                // =========================
                // SUBSCRIPTION METHODS
                // =========================
                subscribeToOrders: (callback: (order: Order) => void) => {
                    orderSubscribers.push(callback);

                    // Return unsubscribe function
                    return () => {
                        orderSubscribers = orderSubscribers.filter(sub => sub !== callback);
                    };
                },

                unsubscribeFromOrders: (callback: (order: Order) => void) => {
                    orderSubscribers = orderSubscribers.filter(sub => sub !== callback);
                },

                subscribeToOrderUpdates: (orderId: string, callback: (order: Order) => void) => {
                    if (!orderSpecificSubscribers[orderId]) {
                        orderSpecificSubscribers[orderId] = [];
                    }

                    orderSpecificSubscribers[orderId].push(callback);

                    // Return unsubscribe function
                    return () => {
                        if (orderSpecificSubscribers[orderId]) {
                            orderSpecificSubscribers[orderId] = orderSpecificSubscribers[orderId]
                                .filter(sub => sub !== callback);

                            if (orderSpecificSubscribers[orderId].length === 0) {
                                delete orderSpecificSubscribers[orderId];
                            }
                        }
                    };
                },

submitReview: async (userId: string, comment?: string) => {
                    if (!userId) {
                        throw new Error("User ID is required to submit a review.");
                    }

                    await simulateApiDelay();
                    
                    const reviewText = comment || "";
                    const newReview = { id: generateUniqueId(), userId, comment: reviewText, createdAt: new Date().toISOString() };
                    
                    set((state: EnhancedOrderState) => ({
                        ...state,
                        reviews: [newReview, ...(state.reviews || [])],
                    })); 
                    
                    // Return the current order after adding the review
                    const currentOrder = get().currentOrder;
                    if (!currentOrder) {
                        throw new Error("No current order to attach review to");
                    }
                    return currentOrder;
                },
                unsubscribeFromOrderUpdates: (orderId: string, callback: (order: Order) => void) => {
                    if (orderSpecificSubscribers[orderId]) {
                        orderSpecificSubscribers[orderId] = orderSpecificSubscribers[orderId]
                            .filter(sub => sub !== callback);

                        if (orderSpecificSubscribers[orderId].length === 0) {
                            delete orderSpecificSubscribers[orderId];
                        }
                    }
                },

                // =========================
                // ASYNC ACTIONS (simulate "API" latency)
                // =========================

                fetchOrders: async (params) => {
                    set({ isLoading: true, error: null });
                    try {
                        await simulateApiDelay();

                        let filtered = [...get().orders];
                        if (params?.restaurantId)
                            filtered = filtered.filter(
                                (o) => o.restaurantId === params.restaurantId
                            );
                        if (params?.customerId)
                            filtered = filtered.filter(
                                (o) => o.customerId === params.customerId
                            );
                        if (params?.waiterId)
                            filtered = filtered.filter(
                                (o) =>
                                    o.primaryWaiterId === params.waiterId ||
                                    (o.assignedWaiters &&
                                        o.assignedWaiters.some(
                                            (w) => w.waiterId === params.waiterId
                                        ))
                            );

                        set({
                            orders: filtered,
                            isLoading: false,
                            pagination: { ...get().pagination, total: filtered.length },
                        });
                        return filtered;
                    } catch (error) {
                        set({ error: "Failed to fetch orders", isLoading: false });
                        return [];
                    }
                },

                fetchOrderById: async (orderId) => {
                    set({ isLoading: true, error: null });
                    try {
                        await simulateApiDelay();
                        const order = get().orders.find((o) => o.id === orderId) || null;
                        set({ currentOrder: order, isLoading: false });
                        return order;
                    } catch {
                        set({ error: "Failed to fetch order", isLoading: false });
                        return null;
                    }
                },

                fetchOrdersByRestaurantId: async (restaurantId: string) => {
                    set({ isLoading: true, error: null });
                    try {
                        await simulateApiDelay();
                        const orders = get().orders.filter((o) => o.restaurantId === restaurantId);
                        set({ orders: orders, isLoading: false });
                        return orders;
                    } catch {
                        set({ error: "Failed to fetch orders", isLoading: false });
                        return [];
                    }
                },

                fetchOrderByNumber: async (orderNumber) => {
                    set({ isLoading: true, error: null });
                    try {
                        await simulateApiDelay();
                        const order =
                            get().orders.find((o) => o.orderNumber === orderNumber) || null;
                        set({ currentOrder: order, isLoading: false });
                        return order;
                    } catch {
                        set({ error: "Failed to fetch order", isLoading: false });
                        return null;
                    }
                },

                fetchOrderByTrackingCode: async (trackingCode) => {
                    set({ isLoading: true, error: null });
                    try {
                        await simulateApiDelay();
                        const order =
                            get().orders.find((o) => o.trackingCode === trackingCode) || null;
                        set({ currentOrder: order, isLoading: false });
                        return order;
                    } catch {
                        set({ error: "Failed to fetch order", isLoading: false });
                        return null;
                    }
                },

                createOrder: async (cart) => {
                    await simulateApiDelay(400, 1000);
                    const now = new Date().toISOString();
                    const newOrder: Order = {
                        ...cart,
                        id: `order-${generateUniqueId()}`,
                        orderNumber: `ORD-${Math.floor(10000 + Math.random() * 90000)}`,
                        customerId: cart.customerId,
                        restaurantId: cart.restaurantId,
                        items: cart.items,
                        itemIds: cart.items.map((i: { id: string; }) => i.id),
                        subtotal: cart.subtotal ?? 0,
                        tax: cart.tax ?? 0,
                        discountAmount: cart.discountAmount || 0,
                        trackingCode: `TRK-${generateUniqueId()}`,
                        status: "pending",
                        createdAt: now,
                        updatedAt: now,
                        assignedWaiters: [],
                        serviceCharge: cart.serviceCharge ?? 0,
                        deliveryFee: cart.deliveryFee ?? 0,
                        total: cart.total ?? 0,
                        partySize: cart.partySize || 1,
                        specialInstructions: cart.specialInstructions || "",
                        orderType: cart.orderType,
                        paymentStatus: cart.paymentStatus,
                        paidAt: cart.paidAt,
                        loyaltyUsed: cart.loyaltyUsed,
                        paymentMethodId: cart.paymentMethodId,
                        paymentTransactionId: cart.paymentTransactionId,
                        estimatedPreparationTime: cart.estimatedPreparationTime,
                        tipAmount: cart.tipAmount || 0,
                        statusHistory: [
                            {
                                status: "pending",
                                timestamp: now,
                                note: "Order created",
                                userId: cart.customerId,
                            },
                        ],
                    };

                    set((state) => ({
                        orders: [newOrder, ...state.orders],
                        currentOrder: newOrder,
                        pagination: {
                            ...state.pagination,
                            total: state.pagination.total + 1,
                        },
                    }));

                    // Simulate real-time broadcast
                    socket?.emit("order:new", newOrder);
                    return newOrder;
                },

                // Enhanced updateOrderStatus implementation
                updateOrderStatus: async (orderId: string, status: OrderStatus, note?: string) => {
                    await simulateApiDelay();

                    let updatedOrder: Order | undefined;

                    set((state) => {
                        // Fixed: Use state.currentUserId instead of state.customerId
                        const userId = state.currentUserId || "system";

                        const updatedOrders = state.orders.map((order) => {
                            if (order.id === orderId) {
                                // Validate status transition
                                if (order.status && !validateOrderStatusTransition(order.status, status)) {
                                    throw new Error(`Invalid status transition from ${order.status} to ${status}`);
                                }

                                const statusUpdate = {
                                    status,
                                    timestamp: new Date().toISOString(),
                                    note: note || "",
                                    userId: userId,
                                };

                                // Calculate completion and payment timestamps
                                const completedAt = status === 'completed' ? new Date().toISOString() : order.completedAt;
                                const shouldUpdatePaidAt = (status === 'completed' || status === 'served') &&
                                    order.paymentStatus === 'paid' &&
                                    !order.paidAt;
                                const paidAt = shouldUpdatePaidAt ? new Date().toISOString() : order.paidAt;

                                updatedOrder = {
                                    ...order,
                                    status,
                                    updatedAt: new Date().toISOString(),
                                    completedAt,
                                    paidAt,
                                    statusHistory: [
                                        ...(order.statusHistory || []),
                                        statusUpdate
                                    ],
                                };

                                return updatedOrder;
                            }
                            return order;
                        });

                        return { orders: updatedOrders };
                    });

                    if (!updatedOrder) {
                        throw new Error(`Order with ID ${orderId} not found`);
                    }

                    // Emit socket events for real-time updates
                    if (['ready', 'served', 'completed'].includes(status)) {
                        socket?.emit("order:ready", {
                            orderId: updatedOrder.id,
                            status: updatedOrder.status,
                            tableId: updatedOrder.tableId,
                            customerId: updatedOrder.customerId,
                            estimatedServeTime: updatedOrder.estimatedServeTime,
                        });
                    }

                    if (status === 'cancelled') {
                        socket?.emit("order:cancelled", {
                            orderId: updatedOrder.id,
                            reason: note || "No reason provided",
                            userId: updatedOrder.statusHistory?.[updatedOrder.statusHistory.length - 1]?.userId,
                        });
                    }

                    // General status update event
                    socket?.emit("order:status_update", {
                        orderId: updatedOrder.id,
                        status: updatedOrder.status,
                        previousStatus: updatedOrder.statusHistory?.[updatedOrder.statusHistory.length - 2]?.status,
                        timestamp: new Date().toISOString(),
                        userId: updatedOrder.statusHistory?.[updatedOrder.statusHistory.length - 1]?.userId,
                    });

                    // Full order update event
                    socket?.emit("order:update", updatedOrder);

                    return updatedOrder;
                },

                updateOrder: async (orderId, updates) => {
                    await simulateApiDelay();
                    let updatedOrder: Order | undefined;

                    set((state) => ({
                        orders: state.orders.map((order) => {
                            if (order.id === orderId) {
                                updatedOrder = {
                                    ...order,
                                    ...updates,
                                    updatedAt: new Date().toISOString(),
                                };
                                return updatedOrder;
                            }
                            return order;
                        }),
                    }));

                    if (updatedOrder) {
                        socket?.emit("order:update", updatedOrder);
                        return updatedOrder;
                    }

                    throw new Error("Order not found");
                },

                cancelOrder: async (orderId, reason) => {
                    await simulateApiDelay();
                    let updatedOrder: Order | undefined;
                    set((state) => ({
                        orders: state.orders.map((order) => {
                            if (order.id === orderId) {
                                updatedOrder = {
                                    ...order,
                                    status: "cancelled",
                                    updatedAt: new Date().toISOString(),
                                    statusHistory: [
                                        ...(order.statusHistory || []),
                                        {
                                            status: "cancelled",
                                            timestamp: new Date().toISOString(),
                                            note: reason || "",
                                        },
                                    ],
                                };
                                return updatedOrder;
                            }
                            return order;
                        }),
                    }));
                    if (updatedOrder) socket?.emit("order:update", updatedOrder);
                    if (!updatedOrder) throw new Error("Order not found");
                    return updatedOrder;
                },

                addTip: async (orderId, amount, waiterId) => {
                    await simulateApiDelay();
                    let updatedOrder: Order | undefined;
                    set((state) => ({
                        orders: state.orders.map((order) => {
                            if (order.id === orderId) {
                                updatedOrder = {
                                    ...order,
                                    tipAmount: (order.tipAmount || 0) + amount,
                                    assignedWaiters: waiterId
                                        ? (order.assignedWaiters ?? []).map((w) =>
                                            w.waiterId === waiterId
                                                ? { ...w, assignedAt: w.assignedAt }
                                                : w
                                        )
                                        : order.assignedWaiters,
                                };
                                return updatedOrder;
                            }
                            return order;
                        }),
                    }));
                    if (updatedOrder) socket?.emit("order:update", updatedOrder);
                    if (!updatedOrder) throw new Error("Order not found");
                    return updatedOrder;
                },

                assignWaiter: async (orderId, waiterId) => {
                    await simulateApiDelay();
                    let updatedOrder: Order | undefined;
                    set((state) => ({
                        orders: state.orders.map((order) => {
                            if (order.id === orderId) {
                                updatedOrder = { ...order, primaryWaiterId: waiterId };
                                return updatedOrder;
                            }
                            return order;
                        }),
                    }));
                    if (updatedOrder) socket?.emit("order:update", updatedOrder);
                    if (!updatedOrder) throw new Error("Order not found");
                    return updatedOrder;
                },

                printReceipt: async (orderId) => {
                    await simulateApiDelay(100, 400);
                    return { receiptUrl: `/receipts/${orderId}.pdf` };
                },

                printKitchenTicket: async (orderId) => {
                    await simulateApiDelay(100, 400);
                    return { ticketUrl: `/kitchen-tickets/${orderId}.pdf` };
                },

                generateInvoice: async (orderId) => {
                    await simulateApiDelay(100, 400);
                    return `/invoices/${orderId}.pdf`;
                },

                splitBill: async (orderId, parts) => {
                    await simulateApiDelay();
                    let updatedOrder: Order | null = null;

                    set((state) => {
                        const updatedOrders = state.orders.map((order) => {
                            if (order.id === orderId) {
                                // Calculate amount for each part based on items
                                const partsWithCartItems = parts.map((part) => {
                                    // Find the cart items for this part
                                    const partItems = part.items
                                        .map((itemId) => order.items.find((item: CartItem) => item.id === itemId))
                                        .filter((item): item is CartItem => !!item);

                                    // Calculate the total amount for these items
                                    const amount = partItems.reduce((total, item) => total + (item.price * item.quantity), 0);

                                    return {
                                        id: generateUniqueId(),
                                        name: part.name,
                                        itemIds: part.items,
                                        items: partItems,
                                        subtotal: amount,
                                        tax: 0,
                                        serviceCharge: 0,
                                        total: amount,
                                        isPaid: false,
                                        customerId: part.payeeId,
                                        payeeId: part.payeeId,
                                    };
                                });

                                // Calculate proportional taxes and fees for each part
                                const orderSubtotal = order.subtotal || 0;
                                const orderTax = order.tax || 0;
                                const orderServiceCharge = order.serviceCharge || 0;
                                const orderDeliveryFee = order.deliveryFee || 0;

                                const updatedParts = partsWithCartItems.map(part => {
                                    const proportion = orderSubtotal > 0 ? part.subtotal / orderSubtotal : 0;

                                    return {
                                        ...part,
                                        tax: orderTax * proportion,
                                        serviceCharge: orderServiceCharge * proportion,
                                        deliveryFee: orderDeliveryFee * proportion,
                                        total: part.subtotal + (orderTax * proportion) +
                                            (orderServiceCharge * proportion) +
                                            (orderDeliveryFee * proportion)
                                    };
                                });

                                // Create the updated order with split bill information
                                const newOrder: Order = {
                                    ...order,
                                    splitBill: {
                                        isActive: true,
                                        parts: updatedParts,
                                        splitMethod: 'by_items',
                                        totalAmount: order.total || 0,
                                        createdBy: order.customerId,
                                        createdAt: new Date().toISOString(),
                                    },
                                    updatedAt: new Date().toISOString(),
                                };

                                updatedOrder = newOrder;
                                return newOrder;
                            }
                            return order;
                        });

                        return { orders: updatedOrders };
                    });

                    if (updatedOrder) {
                        socket?.emit("order:update", updatedOrder);
                        return updatedOrder;
                    }

                    throw new Error("Order not found");
                },

                // =========================
                // FILTERS & PAGINATION
                // =========================

                setFilter: (filter) => set({ filter }),
                setSort: (sort) => set({ sort }),
                setSearchQuery: (query) => set({ searchQuery: query }),
                setDateRange: (start, end) => {
                    set({ dateRange: { start, end } });
                    return { start, end };
                },
                setPage: (page) => {
                    set((state) => ({
                        pagination: { ...state.pagination, page },
                    }));
                    return page;
                },
            };
        },
        {
            name: "orders-storage",
            partialize: (state) => ({
                orders: state.orders,
                filter: state.filter,
                sort: state.sort,
                searchQuery: state.searchQuery,
                dateRange: state.dateRange,
                pagination: state.pagination,
            }),
            version: 1,
            migrate: (persistedState, version) => persistedState,
        }
    )
);